import React, { useEffect, useRef, useCallback } from 'react';
import './Modal.css';

const Modal = ({ 
  title, 
  children, 
  onClose, 
  size = 'medium',
  transparentBackdrop = false,
  positionRef = null,
  highlightElement = null,
  customWidth = null,
  customMaxWidth = null,
  customHeight = null,
  customMaxHeight = null,
  headerStyle = {},
  bodyStyle = {},
  parameterStyle = false
}) => {
  const modalContentRef = useRef(null);

  // Calculate position if positionRef is provided
  const updatePosition = useCallback(() => {
    if (positionRef && modalContentRef.current) {
      const refElement = positionRef?.current || positionRef;
      if (refElement) {
        try {
          const rect = refElement.getBoundingClientRect();
          modalContentRef.current.style.setProperty('--position-right', `${rect.right}px`);
        } catch (e) {
          console.warn('Error calculating modal position:', e);
        }
      }
    }
  }, [positionRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // Only lock body scroll if not transparent backdrop (to allow map interaction)
    if (!transparentBackdrop) {
      document.body.style.overflow = 'hidden';
    }

    // Apply highlighting to target element
    if (highlightElement) {
      const element = typeof highlightElement === 'string' 
        ? document.querySelector(highlightElement)
        : highlightElement?.current || highlightElement;
      
      if (element) {
        element.classList.add('modal-highlighted');
      }
    }

    // Initial position calculation - use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      updatePosition();
    }, 0);

    // Update position on window resize
    if (positionRef) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleEscape);
      if (!transparentBackdrop) {
        document.body.style.overflow = 'unset';
      }
      
      // Remove highlighting
      if (highlightElement) {
        const element = typeof highlightElement === 'string' 
          ? document.querySelector(highlightElement)
          : highlightElement?.current || highlightElement;
        
        if (element) {
          element.classList.remove('modal-highlighted');
        }
      }

      // Remove resize/scroll listeners
      if (positionRef) {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      }
    };
  }, [onClose, transparentBackdrop, positionRef, highlightElement, updatePosition]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const contentStyle = {};
  if (customWidth) contentStyle.width = customWidth;
  if (customMaxWidth) contentStyle.maxWidth = customMaxWidth;
  if (customHeight) contentStyle.height = customHeight;
  if (customMaxHeight) contentStyle.maxHeight = customMaxHeight;

  return (
    <div 
      className={`modal-backdrop ${transparentBackdrop ? 'transparent' : ''}`} 
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalContentRef}
        className={`modal-content ${size} ${positionRef ? 'positioned' : 'centered'} ${parameterStyle ? 'parameter-style' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={contentStyle}
      >
        <div className="modal-header" style={headerStyle}>
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body" style={bodyStyle}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;